import React, { useRef, useState, useEffect, useLayoutEffect, Fragment, useCallback } from "react";


import TimelineSection from "../timeline/TimelineSection";
import TimelineNumberDisplay from "../timeline/TimelineNumberDisplay";
import TimelineProgressBar from "../timeline/TimelineProgressBar";
import TimelineIntroImage from "../timeline/TimelineIntroImage";
import TimelineBodyText from "../timeline/TimelineBodyText";
import TimelineScrollHint from "../timeline/TimelineScrollHint";

import {
  motion,
  useViewportScroll,
  useTransform,
  useSpring
} from "framer-motion";


import { AnniversarySvg } from "@assets/js/icons";
import isTrue from "@assets/js/is-true";
import { log } from "@assets/js/utils";

import ViewportSizes from "@assets/js/viewport-sizes.js";
import onMediaChanged from "@assets/js/on-media-changed.js";
import getViewportSize from "@assets/js/get-viewport-size.js";
import "./timeline-controller.scss";

const MULT_FACTORS = [0.906, 0.998, 1.05, 1.1];


const TimelineController = ({fields}) => {

  const [ANIM_DUR] = useState(0.3);
  const [DEBUG_MODE] = useState(false);

  // section-wrapper ref
  const scrollRef = useRef(null);

  // scroll-container ref
  const scrollContainer = useRef(null);
   
  // the 'fake' ghost div taking up sufficient space vertically, so horizontal scroll can be mapped
  const ghostRef = useRef(null);
  
  const [currentScrollProgressPct, setCurrentScrollProgressPct] = useState(0);
  const [scrollRange, setScrollRange] = useState(0);
  
  const [viewportSize, setViewportSize] = useState(0);

  const [sectionPositions, setSectionPositions] = useState(null);
    
  // watches user scroll position
  const { scrollYProgress } = useViewportScroll();
  
  // maps vertical scroll position to horizontal for smooth scroll animation
  const scrollYRange = useTransform(
    scrollYProgress,
    [0, 1],
    [0, -scrollRange + viewportSize]
  );
  
  // physics modifiers for the scroll transform  
  const scrollPhysics = { damping: 12, mass: 0.37, stiffness: 75 };
  const horizontalScroll = useSpring(scrollYRange, scrollPhysics);


  // multiplication factor for hash position scrolling 
  // - this is a cop-out for finding the right formula behind setting scroll position

  const [currentMultFactor, setCurrentMultFactor] = useState(MULT_FACTORS[0]);
    
  // flag for avoiding multiple change handlers
  const [changeHandlerAdded,setChangeHandlerAdded] = useState(false);
  
  // values for animating year texts
  const [numberDisplayValues, setNumberDisplayValues] = useState(null);
  

  useLayoutEffect(() => {
    if ( typeof window !== undefined ) {        
      setViewportSize(window.innerWidth);    
    }
  },[])
  
  useEffect(() => {
    if ( sectionPositions && sectionPositions.length > 0 ) {

      // parsing of fields section data and content horizontal position thresholds
      // will generate a list of number display data objects

      let vals = [];
      vals = fields.sections.map(item=>item.fields.headline.value);
      vals.unshift(fields.introHeadline1.value);
      
      let valCount = vals.length;
      
      let parsedVals = [];
      
      for (let j = 0; j < valCount; j++) {

        let valC = vals[j].split('');
        let valN = j < valCount-1 ? vals[j+1].split('') : null;
        
        if ( valC.length && valN) {

          let shared = [];
          let current = [];
          let next = [];
          
          
          // parsed year values
          // compares current and next text values and saves shared letters/numbers
          
          // example - current 1920, next 1960 – will result in
          // shared: 19
          // current: 20
          // next: 60

          for (let k = 0; k < valC.length; k++) {
            if ( valC[k] === valN[k] ) {
              shared.push(valC[k]);
            } else {
              current = valC.slice(k);
              next = valN.slice(k);
              break;
            }
          }
          
          // adjust for intro section object (which is sectionPositions[0]) and uses next item threshold

          parsedVals.push(
            {
              shared: shared.join(''),
              current: current.join(''),
              next: next.join(''),
              currentIsDecade: j > 0 ? isTrue(fields?.sections[j-1]?.fields?.isDecade?.value) : false,
              nextIsDecade: j > 0 ? isTrue(fields?.sections[j]?.fields?.isDecade?.value) : false,
              threshold: j > 0 ? sectionPositions[j+1] : 0
            }
          );
        }        
      }
      
      // manually set values for intro section
      
      if ( sectionPositions && sectionPositions.length > 1) {
        
        parsedVals[0].threshold = sectionPositions[1];
        parsedVals[0].currentIsDecade = false;
        parsedVals[0].nextIsDecade = isTrue(fields?.sections[0]?.fields?.isDecade?.value);
      }

      if ( DEBUG_MODE ) log("TIMELINE | TND | parsedVals", parsedVals);
      
      setNumberDisplayValues(parsedVals);
    }


  }, [ANIM_DUR, DEBUG_MODE, fields, sectionPositions]);

  const updateScrollContainerHeight = useCallback(() => {
    const currentViewport = getViewportSize();
    if (currentViewport.name === 'SMALL') {
      scrollContainer.current.style.height = `${scrollRef.current.getBoundingClientRect().height}px`;
    } else {
      scrollContainer.current.style.height = null; // use scss height
    }
  }, [scrollRef]);


  // update container function
  // fires on system init, resize AND read more handlers (which change scroll width of container)

  const updateContainer = useCallback(() =>{
    if (scrollRef && scrollRef.current) {
      setScrollRange(scrollRef.current.scrollWidth);
      
      let sectionEls = scrollRef.current.querySelectorAll('.timeline-section');
      if ( sectionEls && sectionEls.length ) {
        let positions = [];
        
        for (let sectionEl of sectionEls) {
          positions.push( sectionEl.offsetLeft / (scrollRef.current.scrollWidth - viewportSize) );
        }      

        if ( DEBUG_MODE ) log("TIMELINE | sectionPositions", positions);
        setSectionPositions(positions);
      }    
    }
  }, [DEBUG_MODE, viewportSize]);


  // adds change handler to scroll system, updates position pct

  useEffect(() => {
    const changeHandler = (y) => {      
      if ( scrollRef ) {
        // setCurrentScrollProgressPct(Math.abs(y) / Math.abs(scrollRef.current.scrollWidth - viewportSize));        
        if ( DEBUG_MODE ) log("TIMELINE | currentScrollProgressPct", Math.trunc(scrollYProgress.current*100));
        setCurrentScrollProgressPct(scrollYProgress.current);        
      }
    };    
    if ( scrollYRange && !changeHandlerAdded ) {      
      setChangeHandlerAdded(true);
      scrollYRange.onChange(changeHandler);
    }
  }, [scrollRange, changeHandlerAdded, scrollYRange, scrollYProgress, DEBUG_MODE]);
  


  // scroll position debug

  useEffect(() => {
    if ( DEBUG_MODE ) log("TIMELINE | currentScrollProgressPct", Math.trunc(currentScrollProgressPct*100), Math.trunc(scrollYProgress.current*100));
  }, [DEBUG_MODE, currentScrollProgressPct, scrollYProgress]); 
  

  // use media listener for changing scroll position multiplication factor

  const handleViewportChange = useCallback((newViewport)=>{
    let mF = 0;
    switch (newViewport.name) {
      case ViewportSizes.SMALL.name:
        mF = MULT_FACTORS[0];
        break;
        case ViewportSizes.MEDIUM.name:
        mF = MULT_FACTORS[1];
        break;
      case ViewportSizes.LARGE.name:
        mF = MULT_FACTORS[2];
        break;
      case ViewportSizes.XLARGE.name:
        mF = MULT_FACTORS[3];
        break;
      default:
        mF = MULT_FACTORS[0];
        break;
    }
    setCurrentMultFactor(mF);
  }, []);


  onMediaChanged((newViewport) => {
    handleViewportChange();
  });
  
  useEffect(() => {
    let vp = getViewportSize();
    handleViewportChange(vp);
  },[handleViewportChange]);


  // adds hash listener, if a hash exists, scroll to it after 0.5s (race condition?)
  // return resizelistener for react cancellation
  
  useLayoutEffect(() => {    
    const onHashChange = ()=>{
      const hash = window.location.hash.slice(1);
      if ( hash.length > 0 ) {      
        const anchoredSection = scrollRef.current.querySelector("[data-anchor='"+hash+"']");
        
        if ( anchoredSection ) {        
          setTimeout(function () {
            const sectionPosition = anchoredSection.offsetLeft;
            // const sectionPosition = (anchoredSection.offsetLeft / (scrollRange - viewportSize)) * scrollRange;
            
            if ( DEBUG_MODE ) log("TIMELINE | anchor pos?", sectionPosition, currentMultFactor);
            if(document) document.querySelector('html').scrollTop = sectionPosition * currentMultFactor;
          }, 400);
        }
      }
    }

    onHashChange();
    
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
    
  }, [DEBUG_MODE, currentMultFactor, viewportSize]);
  
  // adds resize listener
  // return resizelistener for react cancellation

  useEffect(() => {
    updateScrollContainerHeight();
  },[updateScrollContainerHeight]);
  
  useLayoutEffect(() => {   

    updateContainer(); 

    const onResize = ()=>{  

      if ( typeof window !== undefined ) {        
        setViewportSize(window.innerWidth);
      }

      updateContainer();

      // reset scrollContainer height before function update on resize
      scrollContainer.current.style.height = null;
      updateScrollContainerHeight();
        
    };    

    onResize();

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);


  }, [DEBUG_MODE, updateContainer, updateScrollContainerHeight, viewportSize]);

  // try to scroll a bit right to show new text

  const readMoreHandler = () => {
    updateContainer();      

    setTimeout(function () {
      if(document) document.querySelector('html').scrollTop = document.querySelector('html').scrollTop + 350;
    }, 50);
  };

  return (
    <div className="timeline">
      {DEBUG_MODE && <h6>VW: {viewportSize}</h6>}
      { fields.sections && sectionPositions && <>
      
      { fields?.introHeadline1?.value && numberDisplayValues && <div className="timeline__headline">
         <motion.h1 
          className="timeline__headline__primary"
          animate={{ opacity: currentScrollProgressPct < sectionPositions[1] - (ANIM_DUR / numberDisplayValues.length) ? 1 : 0 }}
          transition={{ duration: ANIM_DUR * 1.5 }} >
            <span>
            {fields.introHeadline1.value && fields.introHeadline1.value.split('').map((letter,index)=>{
              return (
                <em key={`intro1Letter${index}`}>{letter}</em>
              );
            })}
            </span>
        </motion.h1>
        {fields?.introHeadline2?.value && <motion.h1 
          className="timeline__headline__secondary"
          animate={{ 
            opacity: currentScrollProgressPct < sectionPositions[1] - (ANIM_DUR / numberDisplayValues.length)*1.2 ? 1 : 0, 
            marginTop: currentScrollProgressPct < sectionPositions[1] - (ANIM_DUR / numberDisplayValues.length)*1.2 ? '-0.1em' : '-1em'
          }}
          transition={{ duration: ANIM_DUR }} >
          <span>
          {fields.introHeadline2.value && fields.introHeadline2.value.split('').map((letter,index)=>{
            return (
              <em key={`intro2Letter${index}`}>{letter}</em>
            );
          })}
          </span>
        </motion.h1>}
      </div> }
      
      {sectionPositions && numberDisplayValues && 
        <TimelineNumberDisplay 
          debugMode={DEBUG_MODE}
          scrollYProgress={scrollYProgress} 
          progressPct={currentScrollProgressPct} 
          values={numberDisplayValues} 
          durPct={ANIM_DUR / numberDisplayValues.length}
          sectionCount={numberDisplayValues.length}/> 
      }
      </>
    }
      <div ref={scrollContainer} className="timeline__scroll-container">

        <motion.section
          ref={scrollRef}
          style={{ x: horizontalScroll }}
          className="timeline__section-wrapper"
        >
          
          <section className="timeline-section" data-anchor="intro" data-index="0">
            <div className="timeline__logo">
              <AnniversarySvg/>
            </div>
            <TimelineIntroImage media={fields.introMedia} isWide={true}/>            
            <TimelineBodyText bodyTextPart1={fields.introBodyTextPart1} bodyTextPart2={fields.introBodyTextPart2}/>
          </section>


          { scrollYProgress && fields.sections && fields.sections.map((item, index) => {
            return (
            <Fragment key={`sectionContent${index}`}> 
              {/* why index + 1 ? becaouse section 0 is the intro */}
              <TimelineSection
                sectionIndex={index+1}
                debugMode={DEBUG_MODE}
                posThreshold={sectionPositions && sectionPositions[index+1] ? sectionPositions[index+1] : 0}
                scrollYProgress={scrollYProgress}
                durPct={ANIM_DUR / fields.sections.length}
                readMoreHandler={readMoreHandler}
                fields={item.fields}
              />
            </Fragment>
            )}
          )}
        </motion.section>
      </div>
      
      {sectionPositions && scrollYProgress && <TimelineProgressBar scrollYProgress={scrollYProgress} thresholds={sectionPositions}/>}

      {!isNaN(currentScrollProgressPct) && <TimelineScrollHint isActive={currentScrollProgressPct < 0.1}/>}
      
      <div
        ref={ghostRef}
        style={{ height: scrollRange }}
        className="timeline__ghost-container"
      ></div>
    </div>
  );
};

export default TimelineController;
